/** Coerce CV list fields (timeline, education, …) to a real array for UI .map(). */
export function coerceCvList<T = Record<string, unknown>>(value: unknown): T[] {
    if (value == null) return [];
    if (Array.isArray(value)) return value as T[];
    if (typeof value === 'string') {
        const s = value.trim();
        return s ? ([{ description: s }] as T[]) : [];
    }
    if (typeof value === 'object') {
        const o = value as Record<string, unknown>;
        if (Array.isArray(o.data)) return o.data as T[];
        const keys = Object.keys(o);
        if (keys.length > 0 && keys.every((k) => /^\d+$/.test(k))) {
            return keys
                .sort((a, b) => Number(a) - Number(b))
                .map((k) => o[k]) as T[];
        }
        if (
            'role' in o ||
            'title' in o ||
            'company' in o ||
            'degree' in o ||
            'school' in o ||
            'name' in o ||
            'language' in o ||
            'level' in o
        ) {
            return [value as T];
        }
    }
    return [];
}

/** Coerce tags / hobbies to string[]. */
export function coerceCvTags(value: unknown): string[] {
    if (value == null) return [];
    if (Array.isArray(value)) {
        return value
            .map((t) => {
                if (typeof t === 'string') return t.trim();
                if (t && typeof t === 'object' && 'name' in (t as object)) {
                    return String((t as { name?: string }).name || '').trim();
                }
                return String(t ?? '').trim();
            })
            .filter(Boolean);
    }
    if (typeof value === 'string') {
        return value
            .split(/[,;|]/)
            .map((s) => s.trim())
            .filter(Boolean);
    }
    return [];
}

/** Normalize all list-shaped fields on a parsed CV / AI analysis object. */
export function normalizeCvAnalysisResult<T extends Record<string, unknown>>(obj: T | null | undefined): T {
    if (!obj || typeof obj !== 'object') return (obj ?? {}) as T;
    const out = { ...obj } as T & Record<string, unknown>;
    for (const key of ['timeline', 'education', 'courses', 'references', 'languages'] as const) {
        if (key in out) out[key] = coerceCvList(out[key]);
    }
    if ('tags' in out) out.tags = coerceCvTags(out.tags);
    if ('hobbies' in out) out.hobbies = coerceCvTags(out.hobbies);
    return out as T;
}
